import React from "react";
import { ThemeProvider, useColorMode, useTheme } from "@chakra-ui/react";
import { getColor } from "../../../../brandConfig";
import { paletteFrom } from "./sopPalette";

/**
 * SopKitScope — the state-of-play module's floor, applied once (POLISH v1, 2026-09-24).
 *
 * Wraps every state-of-play page (see `withSopKit`). Inside it:
 *
 *  1. Four colour schemes built from the TENANT BRANDING RECORD via `getColor(...)`:
 *     `brand` (colors.primary), `success`, `warning`, `danger` (colors.status.*).
 *     Components write `colorScheme="brand"` / `color="success.600"` and get the
 *     tenant's own colour — never Chakra's teal.
 *  2. Chakra's component sizes lifted to the readability floor: every Button /
 *     IconButton / Input / Select / NumberInput / Tab / CloseButton is at least
 *     44px tall whatever `size` a call site passes, and Badge / Tag / Table
 *     header / Code text is never under 14px (no tiny capitals in table heads).
 *
 * It works through React context, so modals, drawers and tooltips that portal to
 * <body> are covered too — a CSS `sx` rule on a wrapper would miss them.
 *
 * Nothing here changes behaviour: it is theme only. Outside a ChakraProvider (a
 * bare unit test) it renders its children untouched.
 */

type StyleFn = Record<string, unknown> | ((props: Record<string, unknown>) => Record<string, unknown>) | undefined;
type ComponentConfig = { baseStyle?: StyleFn; sizes?: Record<string, StyleFn>; [k: string]: unknown };

const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === "object" && !Array.isArray(v);

function deepMerge(a: Record<string, unknown>, b: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = { ...a };
  for (const [k, v] of Object.entries(b)) out[k] = isObj(v) && isObj(out[k]) ? deepMerge(out[k] as Record<string, unknown>, v) : v;
  return out;
}

/** A style (object or function of props) with `patch` merged over what it resolves to. */
const patched = (orig: StyleFn, patch: Record<string, unknown>) => (props: Record<string, unknown>) =>
  deepMerge((typeof orig === "function" ? orig(props) : orig) ?? {}, patch);

function patchSizes(cfg: ComponentConfig | undefined, patches: Record<string, Record<string, unknown>>): ComponentConfig | undefined {
  if (!cfg) return cfg;
  const sizes = { ...(cfg.sizes ?? {}) };
  for (const [size, patch] of Object.entries(patches)) sizes[size] = patched(sizes[size], patch);
  return { ...cfg, sizes };
}

const patchBase = (cfg: ComponentConfig | undefined, patch: Record<string, unknown>): ComponentConfig | undefined =>
  cfg ? { ...cfg, baseStyle: patched(cfg.baseStyle, patch) } : cfg;

const TOUCH = "44px";
const buttonSize = (fontSize: string) => ({ h: TOUCH, minW: TOUCH, fontSize });
// Input-family sizes are CSS variables (`--input-height`, `--input-font-size`) on the field AND the group.
const fieldSize = (fontSize: string) => {
  const v = { "--input-height": TOUCH, "--input-font-size": `var(--chakra-fontSizes-${fontSize})` };
  return { field: v, group: v, addon: v, element: v };
};
const tabSize = { tab: { minH: TOUCH } };
const thFloor = { th: { fontSize: "sm", textTransform: "none", letterSpacing: "normal" } };

function buildScopedTheme(outer: Record<string, unknown>, palettes: Record<string, unknown>) {
  const c = (outer.components ?? {}) as Record<string, ComponentConfig>;
  const components: Record<string, ComponentConfig | undefined> = {
    ...c,
    Button: patchSizes(c.Button, { xs: buttonSize("sm"), sm: buttonSize("sm"), md: buttonSize("md") }),
    Input: patchSizes(c.Input, { xs: fieldSize("sm"), sm: fieldSize("sm"), md: fieldSize("md") }),
    Select: patchSizes(c.Select, { xs: fieldSize("sm"), sm: fieldSize("sm"), md: fieldSize("md") }),
    NumberInput: patchSizes(c.NumberInput, { xs: fieldSize("sm"), sm: fieldSize("sm"), md: fieldSize("md") }),
    Tabs: patchSizes(c.Tabs, { sm: tabSize, md: tabSize }),
    CloseButton: patchSizes(c.CloseButton, { sm: { "--close-button-size": TOUCH, fontSize: "sm" }, md: { "--close-button-size": TOUCH } }),
    Badge: patchBase(c.Badge, { fontSize: "sm" }),
    Tag: patchSizes(c.Tag, { sm: { container: { "--tag-font-size": "var(--chakra-fontSizes-sm)", "--tag-min-height": "var(--chakra-sizes-7)" } } }),
    Table: patchSizes(c.Table, { sm: thFloor, md: thFloor, lg: thFloor }),
    Code: patchBase(c.Code, { fontSize: "sm" }),
  };
  return { ...outer, colors: { ...(outer.colors as Record<string, unknown>), ...palettes }, components };
}

export const SopKitScope: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const outer = useTheme() as Record<string, unknown> | undefined;
  const { colorMode } = useColorMode();
  const mode = colorMode === "light" ? "light" : "dark";
  const brand = getColor("primary", mode);
  const success = getColor("status.success", mode);
  const warning = getColor("status.warning", mode);
  const danger = getColor("status.error", mode);
  const theme = React.useMemo(() => {
    if (!outer || !outer.components) return null;
    const colors = (outer.colors ?? {}) as Record<string, unknown>;
    // An unparseable brand value falls back to the nearest Chakra palette rather than a guess.
    const palettes = {
      brand: paletteFrom(brand) ?? colors.blue,
      success: paletteFrom(success) ?? colors.green,
      warning: paletteFrom(warning) ?? colors.orange,
      danger: paletteFrom(danger) ?? colors.red,
    };
    return buildScopedTheme(outer, palettes);
  }, [outer, brand, success, warning, danger]);
  if (!theme) return <>{children}</>;
  return <ThemeProvider theme={theme as never}>{children}</ThemeProvider>;
};

/** Wrap a state-of-play page so it (and every dialog it opens) renders inside the kit scope. */
export function withSopKit<P extends object>(Page: React.ComponentType<P>): React.FC<P> {
  const Scoped: React.FC<P> = (props) => (
    <SopKitScope>
      <Page {...props} />
    </SopKitScope>
  );
  Scoped.displayName = `withSopKit(${Page.displayName || Page.name || "Page"})`;
  return Scoped;
}

export default SopKitScope;
